import React, { useCallback, useEffect, useState } from "react";
import Button from "../Button";
import { OPEN_ASSISTANT_EVENT } from "../../utils/assistant";
import { useAiCapabilities } from "../../hooks/useAiCapabilities";
import classes from "./assistant.module.scss";

const SEEN_KEY = "crx:ask-hint-seen";

// Storage that can't be read counts as seen: a tip that can't remember being
// dismissed would come back on every page load.
const readSeen = (): boolean => {
    try {
        return window.localStorage.getItem(SEEN_KEY) === "1";
    } catch {
        return true;
    }
};

/**
 * The one-time "Ask CRX here" tip: shown to people with AI on until they
 * dismiss it, open search, or ask anything. Remembered per browser.
 * @param canAsk - Whether AI is on for the user.
 */
export const useAskHint = (canAsk: boolean): { visible: boolean; dismiss: () => void } => {
    const [seen, setSeen] = useState(readSeen);

    const dismiss = useCallback(() => {
        setSeen(true);
        try {
            window.localStorage.setItem(SEEN_KEY, "1");
        } catch {
            // Private window: the tip just won't be remembered.
        }
    }, []);

    useEffect(() => {
        if (seen) return;
        window.addEventListener(OPEN_ASSISTANT_EVENT, dismiss);
        return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, dismiss);
    }, [seen, dismiss]);

    return { visible: canAsk && !seen, dismiss };
};

interface AskHintProps {
    onDismiss: () => void;
    /** Phone: anchored under the search icon at the header's right. */
    isMobile?: boolean;
}

/**
 * The tip bubble, placed under the search box (or the phone's search icon).
 * Its example is a question from the person's own main area, so a CRM user
 * is never shown a missions question.
 */
const AskHint: React.FC<AskHintProps> = ({ onDismiss, isMobile = false }) => {
    const map = useAiCapabilities(null, true);
    const example = map?.areas[0]?.capabilities.find((c) => c.send)?.question;
    return (
        <div className={`${classes.askHint} ${isMobile ? classes.askHintMobile : ""}`} role="status">
            <span className={classes.askHintTitle}><i className="bi bi-stars" aria-hidden="true" /> Ask CRX here</span>
            <span className={classes.askHintText}>
                {isMobile ? "Tap search and type a question" : "Type a question in search"}{example ? `, like “${example}”,` : ""} and press Enter.
                CRX answers from what you can see, beside the page you're on.
            </span>
            <span className={classes.askHintActions}>
                <Button type="button" variant="primary" size="xs" onClick={onDismiss} title="Hide this tip">Got it</Button>
            </span>
        </div>
    );
};

export default AskHint;
